import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import UniversalProgramModal from '@/components/programs/UniversalProgramModal';

/**
 * Test Page for Universal Program Modal
 * 
 * Navigate to /test-universal-modal to test the modal component
 */
const TestUniversalModal = () => {
  const [isDiscoveryModalOpen, setIsDiscoveryModalOpen] = useState(false);
  const [isUserProgramModalOpen, setIsUserProgramModalOpen] = useState(false);

  // Sample camp data for discovery mode
  const sampleCamp = {
    id: 'test-camp-1',
    name: 'Adventure Summer Camp',
    location: 'Boulder, CO',
    formatted_address: '123 Camp Road, Boulder, CO 80301',
    brief_description: 'An exciting summer camp experience with outdoor activities, arts and crafts, and team building exercises. Perfect for kids ages 8-14.',
    editorial_summary: 'Adventure Summer Camp offers a comprehensive outdoor experience combining adventure sports, creative arts, and team-building activities. Our program is designed to build confidence, foster friendships, and create lasting memories in the beautiful Colorado mountains.',
    description: 'An exciting summer camp experience with outdoor activities, arts and crafts, and team building exercises. Perfect for kids ages 8-14.',
    activities: ['Hiking', 'Rock Climbing', 'Swimming', 'Arts & Crafts', 'Team Building', 'Nature Exploration'],
    user_activities: ['Campfire Stories', 'Star Gazing', 'Wildlife Observation'],
    // Schedule details
    user_schedule_details: {
      drop_off: '8:00 AM - 9:00 AM',
      pick_up: '4:00 PM - 5:00 PM',
      meals: ['Lunch', 'Afternoon Snack']
    },
    // Cost details
    user_cost_details: {
      amount: 450,
      per: 'week',
      notes: 'Early bird discount: $400/week if registered before April 1st. Multi-week discounts available.'
    },
    // Important dates
    early_bird_deadline: '2024-04-01',
    registration_deadline: '2024-05-15',
    cancellation_deadline: '2024-05-01',
    rating: 4.7,
    user_ratings_total: 127,
    age_min: 8,
    age_max: 14,
    age_range: 'Ages 8-14',
    program_type: 'Summer Camp',
    price: 450,
    cost: 450,
    cost_structure: '$450 per week, early bird discount available',
    start_date: '2024-06-15',
    end_date: '2024-08-15',
    user_camp_start_date: '2024-06-15',
    user_camp_end_date: '2024-08-15',
    schedule_dates: 'Monday-Friday, 9am-4pm',
    user_session_start_time: '9:00 AM',
    user_session_end_time: '4:00 PM',
    contact_phone: '(303) 555-0123',
    phone: '(303) 555-0123',
    contact_email: 'info@adventurecamp.com',
    website_url: 'https://adventurecamp.com',
    website: 'https://adventurecamp.com',
    coordinates_lat: 40.0150,
    coordinates_lng: -105.2705,
    latitude: 40.0150,
    longitude: -105.2705,
    image_url: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=800',
    photos: [
      { url: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=800' },
      { url: 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=800' }
    ],
    all_categories: ['Outdoor', 'Sports', 'Adventure'],
    primary_category: 'Outdoor',
    user_contributed: false,
    has_community_enhancements: true,
    community_contribution_count: 3,
    data_confidence_score: 87,
    distance: 2.5,
    // Confidence fields for verified icons
    dates_confidence: 'high',
    schedule_confidence: 'high',
    cost_confidence: 'high',
  };

  // Sample user program data
  const sampleUserProgram = {
    id: 'user-program-1',
    status: 'considering',
    child_names: ['Emma', 'Lucas'],
    program_data: {
      name: 'Adventure Summer Camp',
      location: 'Boulder, CO',
      registration_deadline: '2024-05-15',
      early_bird_deadline: '2024-04-01',
      notes: 'Emma is really excited about this one. Need to check with Lucas if he wants to attend too.',
      booking_confirmation: 'ABC123456',
      refund_policy: 'Full refund if cancelled 30 days before start date. 50% refund if cancelled 14 days before.',
    },
  };

  // Sample profile data with children
  const sampleProfile = {
    children: [
      { name: 'Emma', age: 10, photo_url: null },
      { name: 'Lucas', age: 8, photo_url: null },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Universal Program Modal Test
          </h1>
          <p className="text-gray-600">
            Test the Universal Program Modal in both discovery and user program modes
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Discovery Mode Test */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Discovery Mode
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Test the modal for viewing programs from search/discovery
            </p>
            <Button
              onClick={() => setIsDiscoveryModalOpen(true)}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white"
            >
              Open Discovery Modal
            </Button>
          </div>

          {/* User Program Mode Test */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              User Program Mode
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Test the modal for viewing/editing saved user programs
            </p>
            <Button
              onClick={() => setIsUserProgramModalOpen(true)}
              variant="outline"
              className="w-full"
            >
              Open User Program Modal
            </Button>
          </div>
        </div>

        {/* Sample Data Display */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Sample Data
          </h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Camp Data:</h3>
              <pre className="bg-gray-50 p-4 rounded text-xs overflow-auto max-h-40">
                {JSON.stringify(sampleCamp, null, 2)}
              </pre>
            </div>
            <div>
              <h3 className="font-medium text-gray-700 mb-2">User Program Data:</h3>
              <pre className="bg-gray-50 p-4 rounded text-xs overflow-auto max-h-40">
                {JSON.stringify(sampleUserProgram, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* Discovery Mode Modal */}
      <UniversalProgramModal
        camp={sampleCamp}
        mode="discovery"
        isOpen={isDiscoveryModalOpen}
        onClose={() => setIsDiscoveryModalOpen(false)}
        onSaveForLater={() => {
          console.log('Save for later clicked');
          alert('Save for later functionality would be implemented here');
        }}
        onShare={() => {
          console.log('Share clicked');
          if (navigator.share) {
            navigator.share({
              title: sampleCamp.name,
              text: `Check out ${sampleCamp.name}!`,
              url: window.location.href,
            });
          } else {
            alert('Share functionality would be implemented here');
          }
        }}
        onGetDirections={() => {
          console.log('Get directions clicked');
          const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(sampleCamp.location)}`;
          window.open(mapsUrl, '_blank');
        }}
      />

      {/* User Program Mode Modal */}
      <UniversalProgramModal
        camp={sampleCamp}
        mode="user_program"
        isOpen={isUserProgramModalOpen}
        onClose={() => setIsUserProgramModalOpen(false)}
        userProgram={sampleUserProgram}
        profile={sampleProfile}
        onSave={(updatedProgram) => {
          console.log('Save clicked', updatedProgram);
          alert('Save functionality would be implemented here');
          setIsUserProgramModalOpen(false);
        }}
        onDelete={() => {
          console.log('Delete clicked');
          alert('Delete functionality would be implemented here');
        }}
      />
    </div>
  );
};

export default TestUniversalModal;

